import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Mail, Phone, RefreshCw, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "raua_admin_token";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && e.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back.");
      onSuccess();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#b8965a]/40 text-[#1f3a2e] placeholder-[#6b7a70]/70 font-body-lux font-light py-3 focus:outline-none focus:border-[#b8965a] transition-colors duration-300";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#faf6ef] px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <span className="font-serif-lux text-3xl tracking-tight text-[#1f3a2e]">
            roam<span className="text-[#b8965a]">around</span>uae
          </span>
          <p className="font-body-lux text-xs uppercase tracking-[0.3em] text-[#9c7d44] mt-3">
            Admin — Consultation Leads
          </p>
        </div>
        <form data-testid="admin-login-form" onSubmit={submit} className="space-y-8">
          <div>
            <label className="font-body-lux text-xs uppercase tracking-[0.2em] text-[#6b7a70] mb-2 block">
              Email
            </label>
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className={inputClass}
              required
            />
          </div>
          <div>
            <label className="font-body-lux text-xs uppercase tracking-[0.2em] text-[#6b7a70] mb-2 block">
              Password
            </label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={inputClass}
              required
            />
          </div>
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full font-body-lux text-sm tracking-wide bg-[#b8965a] text-[#fdfbf7] px-10 py-4 hover:bg-[#9c7d44] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-3"
          >
            {loading && <Loader2 size={18} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function DashboardView({ onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem(TOKEN_KEY);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/consultations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Could not load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-[#faf6ef]">
      <header className="border-b border-[#b8965a]/25 bg-[#faf6ef]/90 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div>
            <span className="font-serif-lux text-2xl tracking-tight text-[#1f3a2e]">
              roam<span className="text-[#b8965a]">around</span>uae
            </span>
            <span className="font-body-lux text-[10px] uppercase tracking-[0.25em] text-[#9c7d44] ml-3">
              Leads
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={fetchLeads}
              className="flex items-center gap-2 font-body-lux text-sm text-[#38473e] border border-[#b8965a]/40 px-4 py-2 hover:bg-[#efe4d1] transition-colors duration-300"
            >
              <RefreshCw size={16} strokeWidth={1.5} />
              Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="flex items-center gap-2 font-body-lux text-sm text-[#fdfbf7] bg-[#1f3a2e] px-4 py-2 hover:bg-[#25523f] transition-colors duration-300"
            >
              <LogOut size={16} strokeWidth={1.5} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-baseline justify-between mb-8">
          <h1 className="font-serif-lux font-light text-4xl text-[#1f3a2e]">
            Consultation Requests
          </h1>
          <span data-testid="admin-lead-count" className="font-body-lux text-sm text-[#6b7a70]">
            {leads.length} total
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32 text-[#9c7d44]">
            <Loader2 size={28} className="animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="flex flex-col items-center justify-center py-32 text-center"
          >
            <Inbox size={40} strokeWidth={1} className="text-[#b8965a] mb-4" />
            <p className="font-serif-lux text-2xl text-[#1f3a2e]">No requests yet</p>
            <p className="font-body-lux text-sm text-[#6b7a70] mt-2">
              New consultation requests will appear here.
            </p>
          </div>
        ) : (
          <div data-testid="admin-leads-list" className="space-y-4">
            {leads.map((lead) => (
              <div
                key={lead.id}
                data-testid={`admin-lead-${lead.id}`}
                className="bg-[#fdfbf7] border border-[#b8965a]/20 p-6 md:p-7 hover:border-[#b8965a]/50 transition-colors duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-serif-lux text-2xl text-[#1f3a2e]">
                        {lead.name}
                      </h3>
                      {lead.travel_dates && (
                        <span className="font-body-lux text-xs uppercase tracking-wide text-[#9c7d44] border border-[#b8965a]/30 px-2 py-1">
                          {lead.travel_dates}
                        </span>
                      )}
                      {lead.party_size && (
                        <span className="font-body-lux text-xs text-[#6b7a70]">
                          {lead.party_size}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3">
                      <a
                        href={`mailto:${lead.email}`}
                        className="flex items-center gap-2 font-body-lux text-sm text-[#38473e] hover:text-[#9c7d44] transition-colors duration-300"
                      >
                        <Mail size={15} strokeWidth={1.4} />
                        {lead.email}
                      </a>
                      <a
                        href={`tel:${lead.phone}`}
                        className="flex items-center gap-2 font-body-lux text-sm text-[#38473e] hover:text-[#9c7d44] transition-colors duration-300"
                      >
                        <Phone size={15} strokeWidth={1.4} />
                        {lead.phone}
                      </a>
                    </div>
                    {lead.interests && (
                      <p className="font-body-lux text-sm font-light text-[#47564d] mt-4 leading-relaxed max-w-2xl">
                        {lead.interests}
                      </p>
                    )}
                  </div>
                  <span className="font-body-lux text-xs text-[#6b7a70] whitespace-nowrap">
                    {fmtDate(lead.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking

  const check = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setAuthed(false);
      return;
    }
    try {
      await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAuthed(true);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#faf6ef] text-[#9c7d44]">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  return authed ? (
    <DashboardView onLogout={logout} />
  ) : (
    <LoginView onSuccess={() => setAuthed(true)} />
  );
}
